import type { TSchema } from "@sinclair/typebox";
import {
  AgentEventSchema,
  AgentIdentityParamsSchema,
  AgentIdentityResultSchema,
  AgentParamsSchema,
  AgentWaitParamsSchema,
  PollParamsSchema,
  SendParamsSchema,
  WakeParamsSchema,
} from "./agent.js";
import {
  AgentSummarySchema,
  AgentsFileEntrySchema,
  AgentsFilesGetParamsSchema,
  AgentsFilesGetResultSchema,
  AgentsFilesListParamsSchema,
  AgentsFilesListResultSchema,
  AgentsFilesSetParamsSchema,
  AgentsFilesSetResultSchema,
  AgentsWorkspaceListParamsSchema,
  AgentsWorkspaceListResultSchema,
  AgentsWorkspaceReadParamsSchema,
  AgentsWorkspaceReadResultSchema,
  AgentsWorkspaceWriteParamsSchema,
  AgentsWorkspaceWriteResultSchema,
  SessionArtifactRecordSchema,
  SessionsArtifactsGetParamsSchema,
  SessionsArtifactsGetResultSchema,
  SessionsArtifactsListParamsSchema,
  SessionsArtifactsListResultSchema,
  SessionsArtifactsSummaryParamsSchema,
  SessionsArtifactsSummaryResultSchema,
  SessionsArtifactsRevertParamsSchema,
  SessionsArtifactsCheckpointsParamsSchema,
  FleetProjectSummarySchema,
  FleetProjectsCreateParamsSchema,
  FleetProjectsCreateResultSchema,
  FleetProjectsDeleteParamsSchema,
  FleetProjectsDeleteResultSchema,
  FleetProjectsGetParamsSchema,
  FleetProjectsGetResultSchema,
  FleetProjectsListParamsSchema,
  FleetProjectsListResultSchema,
  FleetProjectsUpdateParamsSchema,
  FleetProjectsUpdateResultSchema,
  FleetWorkPlanApproveParamsSchema,
  FleetWorkPlanApproveResultSchema,
  FleetWorkPlanCreateParamsSchema,
  FleetWorkPlanCreateResultSchema,
  FleetWorkPlanGetParamsSchema,
  FleetWorkPlanGetResultSchema,
  FleetWorkPlanListParamsSchema,
  FleetWorkPlanListResultSchema,
  FleetWorkPlanPublishYouTrackParamsSchema,
  FleetWorkPlanPublishYouTrackResultSchema,
  FleetWorkPlanRecordSchema,
  FleetWorkPlanSpawnUnitParamsSchema,
  FleetWorkPlanSpawnUnitResultSchema,
  FleetWorkPlanSummarySchema,
  FleetWorkUnitSchema,
  FleetSelfImprovementListParamsSchema,
  FleetSelfImprovementListResultSchema,
  FleetSelfImprovementStartParamsSchema,
  FleetSelfImprovementStartResultSchema,
  FleetSelfImprovementStatusParamsSchema,
  FleetSelfImprovementStatusResultSchema,
  FleetSelfImprovementResolveWorkspaceParamsSchema,
  FleetSelfImprovementResolveWorkspaceResultSchema,
  SelfImprovementJobRecordSchema,
  SelfImprovementJobStatusSchema,
  AgentsFleetGraphParamsSchema,
  AgentsFleetGraphResultSchema,
  AgentsListParamsSchema,
  AgentsListResultSchema,
  MultiagentTemplatesListParamsSchema,
  ModelChoiceSchema,
  ModelsListParamsSchema,
  ModelsListResultSchema,
  SkillsBinsParamsSchema,
  SkillsBinsResultSchema,
  SkillsInstallParamsSchema,
  SkillsStatusParamsSchema,
  SkillsUpdateParamsSchema,
} from "./agents-models-skills.js";
import {
  ChannelsLogoutParamsSchema,
  ChannelsStatusParamsSchema,
  ChannelsStatusResultSchema,
  TalkModeParamsSchema,
  WebLoginStartParamsSchema,
  WebLoginWaitParamsSchema,
} from "./channels.js";
import {
  ConfigApplyParamsSchema,
  ConfigGetParamsSchema,
  ConfigPatchParamsSchema,
  ConfigSchemaParamsSchema,
  ConfigSchemaResponseSchema,
  ConfigSetParamsSchema,
  UpdateRunParamsSchema,
} from "./config.js";
import {
  CronAddParamsSchema,
  CronJobSchema,
  CronListParamsSchema,
  CronRemoveParamsSchema,
  CronRunLogEntrySchema,
  CronRunParamsSchema,
  CronRunsParamsSchema,
  CronStatusParamsSchema,
  CronUpdateParamsSchema,
} from "./cron.js";
import {
  DevicePairApproveParamsSchema,
  DevicePairListParamsSchema,
  DevicePairRejectParamsSchema,
  DevicePairRequestedEventSchema,
  DevicePairResolvedEventSchema,
  DeviceTokenRevokeParamsSchema,
  DeviceTokenRotateParamsSchema,
} from "./devices.js";
import {
  ExecApprovalsGetParamsSchema,
  ExecApprovalsNodeGetParamsSchema,
  ExecApprovalsNodeSetParamsSchema,
  ExecApprovalsSetParamsSchema,
  ExecApprovalsSnapshotSchema,
  ExecApprovalRequestParamsSchema,
  ExecApprovalResolveParamsSchema,
} from "./exec-approvals.js";
import {
  FleetQuestionListParamsSchema,
  FleetQuestionResolveParamsSchema,
  FleetTodosListParamsSchema,
  FleetTodosUpdateParamsSchema,
} from "./fleet-interaction.js";
import {
  ConnectParamsSchema,
  ErrorShapeSchema,
  EventFrameSchema,
  GatewayFrameSchema,
  HelloOkSchema,
  RequestFrameSchema,
  ResponseFrameSchema,
  ShutdownEventSchema,
  TickEventSchema,
} from "./frames.js";
import {
  ChatAbortParamsSchema,
  ChatEventSchema,
  ChatHistoryParamsSchema,
  ChatInjectParamsSchema,
  ChatRegenerateParamsSchema,
  ChatRewindParamsSchema,
  ChatSendParamsSchema,
  ChatSteerParamsSchema,
  FlociAgentsParamsSchema,
  SubagentsListParamsSchema,
  SubagentsCancelAllParamsSchema,
  ProcsetStatusParamsSchema,
  LogsTailParamsSchema,
  LogsTailResultSchema,
} from "./logs-chat.js";
import {
  NodeDescribeParamsSchema,
  NodeEventParamsSchema,
  NodeInvokeParamsSchema,
  NodeInvokeResultParamsSchema,
  NodeInvokeRequestEventSchema,
  NodeListParamsSchema,
  NodePairApproveParamsSchema,
  NodePairListParamsSchema,
  NodePairRejectParamsSchema,
  NodePairRequestParamsSchema,
  NodePairVerifyParamsSchema,
  NodeRenameParamsSchema,
} from "./nodes.js";
import {
  SessionEnvironmentExplainParamsSchema,
  SessionEnvironmentExplainResultSchema,
  SessionEnvironmentRecreateParamsSchema,
  SessionEnvironmentRecreateResultSchema,
} from "./session-environment.js";
import {
  SessionPreviewClearParamsSchema,
  SessionPreviewClearResultSchema,
  SessionPreviewRegisterParamsSchema,
  SessionPreviewRegisterResultSchema,
} from "./session-preview.js";
import {
  SessionsCancelParamsSchema,
  SessionsCancelResultSchema,
  SessionsTurnsLinkChildParamsSchema,
  SessionsCompactParamsSchema,
  SessionsDeleteParamsSchema,
  SessionsListParamsSchema,
  SessionsPatchParamsSchema,
  SessionsPreviewParamsSchema,
  SessionsReorderParamsSchema,
  SessionsReorderResultSchema,
  SessionsResetParamsSchema,
  SessionsResolveParamsSchema,
  SessionsSpawnParamsSchema,
  SessionsContextBreakdownParamsSchema,
  SessionsContextBreakdownResultSchema,
  SessionsPalaceContinuityAddParamsSchema,
  SessionsPalaceContinuityAddResultSchema,
  SessionsPalaceContinuityClearParamsSchema,
  SessionsPalaceContinuityClearResultSchema,
  SessionsPalaceContinuityParamsSchema,
  SessionsPalaceContinuityRemoveParamsSchema,
  SessionsPalaceContinuityRemoveResultSchema,
  SessionsPalaceContinuityResultSchema,
  SessionsUsageParamsSchema,
} from "./sessions.js";
import { PresenceEntrySchema, SnapshotSchema, StateVersionSchema } from "./snapshot.js";
import {
  WizardCancelParamsSchema,
  WizardNextParamsSchema,
  WizardNextResultSchema,
  WizardStartParamsSchema,
  WizardStartResultSchema,
  WizardStatusParamsSchema,
  WizardStatusResultSchema,
  WizardStepSchema,
} from "./wizard.js";
import { WorkforceInsightsParamsSchema, WorkforceRouteSimulateParamsSchema } from "./workforce.js";

export const ProtocolSchemas: Record<string, TSchema> = {
  ConnectParams: ConnectParamsSchema,
  HelloOk: HelloOkSchema,
  RequestFrame: RequestFrameSchema,
  ResponseFrame: ResponseFrameSchema,
  EventFrame: EventFrameSchema,
  GatewayFrame: GatewayFrameSchema,
  PresenceEntry: PresenceEntrySchema,
  StateVersion: StateVersionSchema,
  Snapshot: SnapshotSchema,
  ErrorShape: ErrorShapeSchema,
  AgentEvent: AgentEventSchema,
  SendParams: SendParamsSchema,
  PollParams: PollParamsSchema,
  AgentParams: AgentParamsSchema,
  AgentIdentityParams: AgentIdentityParamsSchema,
  AgentIdentityResult: AgentIdentityResultSchema,
  AgentWaitParams: AgentWaitParamsSchema,
  WakeParams: WakeParamsSchema,
  NodePairRequestParams: NodePairRequestParamsSchema,
  NodePairListParams: NodePairListParamsSchema,
  NodePairApproveParams: NodePairApproveParamsSchema,
  NodePairRejectParams: NodePairRejectParamsSchema,
  NodePairVerifyParams: NodePairVerifyParamsSchema,
  NodeRenameParams: NodeRenameParamsSchema,
  NodeListParams: NodeListParamsSchema,
  NodeDescribeParams: NodeDescribeParamsSchema,
  NodeInvokeParams: NodeInvokeParamsSchema,
  NodeInvokeResultParams: NodeInvokeResultParamsSchema,
  NodeEventParams: NodeEventParamsSchema,
  NodeInvokeRequestEvent: NodeInvokeRequestEventSchema,
  SessionsListParams: SessionsListParamsSchema,
  SessionsPreviewParams: SessionsPreviewParamsSchema,
  SessionsResolveParams: SessionsResolveParamsSchema,
  SessionsPatchParams: SessionsPatchParamsSchema,
  SessionsResetParams: SessionsResetParamsSchema,
  SessionsReorderParams: SessionsReorderParamsSchema,
  SessionsReorderResult: SessionsReorderResultSchema,
  SessionsDeleteParams: SessionsDeleteParamsSchema,
  SessionsCompactParams: SessionsCompactParamsSchema,
  SessionsCancelParams: SessionsCancelParamsSchema,
  SessionsCancelResult: SessionsCancelResultSchema,
  SessionsTurnsLinkChildParams: SessionsTurnsLinkChildParamsSchema,
  SessionsSpawnParams: SessionsSpawnParamsSchema,
  SessionsContextBreakdownParams: SessionsContextBreakdownParamsSchema,
  SessionsContextBreakdownResult: SessionsContextBreakdownResultSchema,
  SessionsPalaceContinuityParams: SessionsPalaceContinuityParamsSchema,
  SessionsPalaceContinuityResult: SessionsPalaceContinuityResultSchema,
  SessionsPalaceContinuityClearParams: SessionsPalaceContinuityClearParamsSchema,
  SessionsPalaceContinuityClearResult: SessionsPalaceContinuityClearResultSchema,
  SessionsPalaceContinuityRemoveParams: SessionsPalaceContinuityRemoveParamsSchema,
  SessionsPalaceContinuityRemoveResult: SessionsPalaceContinuityRemoveResultSchema,
  SessionsPalaceContinuityAddParams: SessionsPalaceContinuityAddParamsSchema,
  SessionsPalaceContinuityAddResult: SessionsPalaceContinuityAddResultSchema,
  SessionsUsageParams: SessionsUsageParamsSchema,
  SessionEnvironmentExplainParams: SessionEnvironmentExplainParamsSchema,
  SessionEnvironmentExplainResult: SessionEnvironmentExplainResultSchema,
  SessionEnvironmentRecreateParams: SessionEnvironmentRecreateParamsSchema,
  SessionEnvironmentRecreateResult: SessionEnvironmentRecreateResultSchema,
  SessionPreviewRegisterParams: SessionPreviewRegisterParamsSchema,
  SessionPreviewRegisterResult: SessionPreviewRegisterResultSchema,
  SessionPreviewClearParams: SessionPreviewClearParamsSchema,
  SessionPreviewClearResult: SessionPreviewClearResultSchema,
  ConfigGetParams: ConfigGetParamsSchema,
  ConfigSetParams: ConfigSetParamsSchema,
  ConfigApplyParams: ConfigApplyParamsSchema,
  ConfigPatchParams: ConfigPatchParamsSchema,
  ConfigSchemaParams: ConfigSchemaParamsSchema,
  ConfigSchemaResponse: ConfigSchemaResponseSchema,
  WizardStartParams: WizardStartParamsSchema,
  WizardNextParams: WizardNextParamsSchema,
  WizardCancelParams: WizardCancelParamsSchema,
  WizardStatusParams: WizardStatusParamsSchema,
  WizardStep: WizardStepSchema,
  WizardNextResult: WizardNextResultSchema,
  WizardStartResult: WizardStartResultSchema,
  WizardStatusResult: WizardStatusResultSchema,
  TalkModeParams: TalkModeParamsSchema,
  ChannelsStatusParams: ChannelsStatusParamsSchema,
  ChannelsStatusResult: ChannelsStatusResultSchema,
  ChannelsLogoutParams: ChannelsLogoutParamsSchema,
  WebLoginStartParams: WebLoginStartParamsSchema,
  WebLoginWaitParams: WebLoginWaitParamsSchema,
  AgentSummary: AgentSummarySchema,
  AgentsFileEntry: AgentsFileEntrySchema,
  AgentsFilesListParams: AgentsFilesListParamsSchema,
  AgentsFilesListResult: AgentsFilesListResultSchema,
  AgentsFilesGetParams: AgentsFilesGetParamsSchema,
  AgentsFilesGetResult: AgentsFilesGetResultSchema,
  AgentsFilesSetParams: AgentsFilesSetParamsSchema,
  AgentsFilesSetResult: AgentsFilesSetResultSchema,
  AgentsWorkspaceListParams: AgentsWorkspaceListParamsSchema,
  AgentsWorkspaceListResult: AgentsWorkspaceListResultSchema,
  AgentsWorkspaceReadParams: AgentsWorkspaceReadParamsSchema,
  AgentsWorkspaceReadResult: AgentsWorkspaceReadResultSchema,
  AgentsWorkspaceWriteParams: AgentsWorkspaceWriteParamsSchema,
  AgentsWorkspaceWriteResult: AgentsWorkspaceWriteResultSchema,
  SessionArtifactRecord: SessionArtifactRecordSchema,
  SessionsArtifactsListParams: SessionsArtifactsListParamsSchema,
  SessionsArtifactsListResult: SessionsArtifactsListResultSchema,
  SessionsArtifactsGetParams: SessionsArtifactsGetParamsSchema,
  SessionsArtifactsGetResult: SessionsArtifactsGetResultSchema,
  SessionsArtifactsSummaryParams: SessionsArtifactsSummaryParamsSchema,
  SessionsArtifactsSummaryResult: SessionsArtifactsSummaryResultSchema,
  SessionsArtifactsRevertParams: SessionsArtifactsRevertParamsSchema,
  SessionsArtifactsCheckpointsParams: SessionsArtifactsCheckpointsParamsSchema,
  FleetProjectSummary: FleetProjectSummarySchema,
  FleetProjectsListParams: FleetProjectsListParamsSchema,
  FleetProjectsListResult: FleetProjectsListResultSchema,
  FleetProjectsCreateParams: FleetProjectsCreateParamsSchema,
  FleetProjectsCreateResult: FleetProjectsCreateResultSchema,
  FleetProjectsGetParams: FleetProjectsGetParamsSchema,
  FleetProjectsGetResult: FleetProjectsGetResultSchema,
  FleetProjectsUpdateParams: FleetProjectsUpdateParamsSchema,
  FleetProjectsUpdateResult: FleetProjectsUpdateResultSchema,
  FleetProjectsDeleteParams: FleetProjectsDeleteParamsSchema,
  FleetProjectsDeleteResult: FleetProjectsDeleteResultSchema,
  FleetWorkUnit: FleetWorkUnitSchema,
  FleetWorkPlanRecord: FleetWorkPlanRecordSchema,
  FleetWorkPlanSummary: FleetWorkPlanSummarySchema,
  FleetWorkPlanListParams: FleetWorkPlanListParamsSchema,
  FleetWorkPlanListResult: FleetWorkPlanListResultSchema,
  FleetWorkPlanGetParams: FleetWorkPlanGetParamsSchema,
  FleetWorkPlanGetResult: FleetWorkPlanGetResultSchema,
  FleetWorkPlanCreateParams: FleetWorkPlanCreateParamsSchema,
  FleetWorkPlanCreateResult: FleetWorkPlanCreateResultSchema,
  FleetWorkPlanApproveParams: FleetWorkPlanApproveParamsSchema,
  FleetWorkPlanApproveResult: FleetWorkPlanApproveResultSchema,
  FleetWorkPlanPublishYouTrackParams: FleetWorkPlanPublishYouTrackParamsSchema,
  FleetWorkPlanPublishYouTrackResult: FleetWorkPlanPublishYouTrackResultSchema,
  FleetWorkPlanSpawnUnitParams: FleetWorkPlanSpawnUnitParamsSchema,
  FleetWorkPlanSpawnUnitResult: FleetWorkPlanSpawnUnitResultSchema,
  SelfImprovementJobStatus: SelfImprovementJobStatusSchema,
  SelfImprovementJobRecord: SelfImprovementJobRecordSchema,
  FleetSelfImprovementStartParams: FleetSelfImprovementStartParamsSchema,
  FleetSelfImprovementStartResult: FleetSelfImprovementStartResultSchema,
  FleetSelfImprovementListParams: FleetSelfImprovementListParamsSchema,
  FleetSelfImprovementListResult: FleetSelfImprovementListResultSchema,
  FleetSelfImprovementStatusParams: FleetSelfImprovementStatusParamsSchema,
  FleetSelfImprovementStatusResult: FleetSelfImprovementStatusResultSchema,
  FleetSelfImprovementResolveWorkspaceParams: FleetSelfImprovementResolveWorkspaceParamsSchema,
  FleetSelfImprovementResolveWorkspaceResult: FleetSelfImprovementResolveWorkspaceResultSchema,
  AgentsFleetGraphParams: AgentsFleetGraphParamsSchema,
  AgentsFleetGraphResult: AgentsFleetGraphResultSchema,
  AgentsListParams: AgentsListParamsSchema,
  AgentsListResult: AgentsListResultSchema,
  MultiagentTemplatesListParams: MultiagentTemplatesListParamsSchema,
  ModelChoice: ModelChoiceSchema,
  ModelsListParams: ModelsListParamsSchema,
  ModelsListResult: ModelsListResultSchema,
  SkillsStatusParams: SkillsStatusParamsSchema,
  SkillsBinsParams: SkillsBinsParamsSchema,
  SkillsBinsResult: SkillsBinsResultSchema,
  SkillsInstallParams: SkillsInstallParamsSchema,
  SkillsUpdateParams: SkillsUpdateParamsSchema,
  CronJob: CronJobSchema,
  CronListParams: CronListParamsSchema,
  CronStatusParams: CronStatusParamsSchema,
  CronAddParams: CronAddParamsSchema,
  CronUpdateParams: CronUpdateParamsSchema,
  CronRemoveParams: CronRemoveParamsSchema,
  CronRunParams: CronRunParamsSchema,
  CronRunsParams: CronRunsParamsSchema,
  CronRunLogEntry: CronRunLogEntrySchema,
  LogsTailParams: LogsTailParamsSchema,
  LogsTailResult: LogsTailResultSchema,
  ExecApprovalsGetParams: ExecApprovalsGetParamsSchema,
  ExecApprovalsSetParams: ExecApprovalsSetParamsSchema,
  ExecApprovalsNodeGetParams: ExecApprovalsNodeGetParamsSchema,
  ExecApprovalsNodeSetParams: ExecApprovalsNodeSetParamsSchema,
  ExecApprovalsSnapshot: ExecApprovalsSnapshotSchema,
  ExecApprovalRequestParams: ExecApprovalRequestParamsSchema,
  ExecApprovalResolveParams: ExecApprovalResolveParamsSchema,
  FleetQuestionResolveParams: FleetQuestionResolveParamsSchema,
  FleetQuestionListParams: FleetQuestionListParamsSchema,
  FleetTodosListParams: FleetTodosListParamsSchema,
  FleetTodosUpdateParams: FleetTodosUpdateParamsSchema,
  DevicePairListParams: DevicePairListParamsSchema,
  DevicePairApproveParams: DevicePairApproveParamsSchema,
  DevicePairRejectParams: DevicePairRejectParamsSchema,
  DeviceTokenRotateParams: DeviceTokenRotateParamsSchema,
  DeviceTokenRevokeParams: DeviceTokenRevokeParamsSchema,
  DevicePairRequestedEvent: DevicePairRequestedEventSchema,
  DevicePairResolvedEvent: DevicePairResolvedEventSchema,
  ChatHistoryParams: ChatHistoryParamsSchema,
  ChatSendParams: ChatSendParamsSchema,
  ChatSteerParams: ChatSteerParamsSchema,
  ChatAbortParams: ChatAbortParamsSchema,
  ChatInjectParams: ChatInjectParamsSchema,
  ChatRewindParams: ChatRewindParamsSchema,
  ChatRegenerateParams: ChatRegenerateParamsSchema,
  FlociAgentsParams: FlociAgentsParamsSchema,
  SubagentsListParams: SubagentsListParamsSchema,
  SubagentsCancelAllParams: SubagentsCancelAllParamsSchema,
  ProcsetStatusParams: ProcsetStatusParamsSchema,
  ChatEvent: ChatEventSchema,
  UpdateRunParams: UpdateRunParamsSchema,
  TickEvent: TickEventSchema,
  ShutdownEvent: ShutdownEventSchema,
  WorkforceRouteSimulateParams: WorkforceRouteSimulateParamsSchema,
  WorkforceInsightsParams: WorkforceInsightsParamsSchema,
};

export const PROTOCOL_VERSION = 3 as const;
